/**
 * The screenshot picker, shared by the new-ticket form and the reply box.
 *
 * Images are compressed and uploaded the moment they are chosen, not on submit. That
 * ordering is the whole design of this component, and it buys three things: the
 * person sees the compression result before they commit to anything, a failed upload
 * costs one file instead of the whole ticket, and submitting is instant because by
 * then there are only object keys to send.
 *
 * The saving is shown deliberately. "4.2 MB → 380 KB" is what makes an unavoidable
 * quality trade-off read as a feature rather than as something done behind
 * somebody's back.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { ACCEPTED_IMAGE_TYPES, formatBytes } from '@/shared/lib/images'
import { useUploadAttachment, type PreparedAttachment } from '@/shared/api/hooks'
import { Button, Notice } from './primitives'

export function AttachmentPicker({
  attachments,
  onChange,
  max,
  maxBytes,
  disabled = false,
}: {
  attachments: PreparedAttachment[]
  onChange: (next: PreparedAttachment[]) => void
  max: number
  maxBytes: number
  disabled?: boolean
}) {
  const upload = useUploadAttachment()
  const inputRef = useRef<HTMLInputElement>(null)
  const [problems, setProblems] = useState<string[]>([])

  // Preview URLs are objects held by the browser until revoked, and each one pins a
  // decoded image in memory. Revoking on unmount is what keeps a person who opens and
  // abandons the form six times from holding six copies.
  const held = useRef<PreparedAttachment[]>(attachments)
  held.current = attachments
  useEffect(
    () => () => {
      for (const attachment of held.current) URL.revokeObjectURL(attachment.previewUrl)
    },
    [],
  )

  const room = max - attachments.length
  const full = room <= 0

  const onFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return

      const chosen = Array.from(files)
      const failures: string[] = []
      const accepted: PreparedAttachment[] = []

      // Only as many as there is room for, and the rest are named rather than
      // silently dropped — a file that vanishes without explanation reads as a bug.
      const taking = chosen.slice(0, Math.max(0, room))
      for (const extra of chosen.slice(taking.length)) {
        failures.push(`${extra.name} was not added — ${max} images is the limit.`)
      }

      for (const file of taking) {
        try {
          accepted.push(await upload.mutateAsync({ file, maxBytes }))
        } catch (cause) {
          failures.push(cause instanceof Error ? cause.message : `${file.name} could not be attached.`)
        }
      }

      if (accepted.length > 0) onChange([...held.current, ...accepted])
      setProblems(failures)
      // Resetting the input matters: without it, choosing the same file again after a
      // failure fires no change event and looks like the button stopped working.
      if (inputRef.current) inputRef.current.value = ''
    },
    [max, maxBytes, onChange, room, upload],
  )

  function remove(key: string) {
    const going = held.current.find((a) => a.key === key)
    if (going) URL.revokeObjectURL(going.previewUrl)
    onChange(held.current.filter((a) => a.key !== key))
  }

  return (
    <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
      <div className="row" style={{ gap: 'var(--s3)' }}>
        <Button
          type="button"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || full}
          loading={upload.isPending}
        >
          {upload.isPending ? 'Compressing…' : 'Attach screenshots'}
        </Button>
        <span className="mono-note">
          {attachments.length}/{max} · PNG, JPEG or WebP · compressed in your browser before
          upload
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(',')}
          multiple
          hidden
          onChange={(event) => void onFiles(event.target.files)}
        />
      </div>

      {problems.length > 0 && (
        <Notice tone="warn">
          <span className="stack" style={{ ['--gap' as string]: 'var(--s1)' }}>
            {problems.map((problem) => (
              <span key={problem}>{problem}</span>
            ))}
          </span>
        </Notice>
      )}

      {attachments.length > 0 && (
        <ul
          className="row"
          style={{ gap: 'var(--s3)', listStyle: 'none', padding: 0, margin: 0, alignItems: 'flex-start' }}
        >
          {attachments.map((attachment) => (
            <li
              key={attachment.key}
              className="stack"
              style={{ ['--gap' as string]: 'var(--s1)', width: 132 }}
            >
              <img
                src={attachment.previewUrl}
                alt={attachment.filename}
                style={{
                  width: '100%',
                  height: 84,
                  objectFit: 'cover',
                  border: '1px solid var(--rule-bright)',
                  borderRadius: 'var(--radius)',
                  display: 'block',
                }}
              />
              <span className="mono-note" style={{ overflowWrap: 'anywhere' }}>
                {formatBytes(attachment.originalBytes)} → {formatBytes(attachment.sizeBytes)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(attachment.key)}
                disabled={disabled}
                style={{ paddingLeft: 0 }}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Renders stored attachments on a ticket or comment.
 *
 * The URLs are signed and short-lived, so an attachment whose signature could not be
 * minted renders as its filename rather than as a broken image icon — the metadata is
 * still useful, and a broken image reads as data loss when nothing was lost.
 */
export function AttachmentStrip({
  attachments,
}: {
  attachments: Array<{ id: string; filename: string; url?: string; width?: number; height?: number }>
}) {
  if (attachments.length === 0) return null

  return (
    <ul
      className="row"
      style={{ gap: 'var(--s3)', listStyle: 'none', padding: 0, margin: 0, alignItems: 'flex-start' }}
    >
      {attachments.map((attachment) =>
        attachment.url ? (
          <li key={attachment.id}>
            {/* A new tab rather than a lightbox: the full-size image in its own tab is
                zoomable, savable and needs no code to dismiss. */}
            <a href={attachment.url} target="_blank" rel="noreferrer" title={attachment.filename}>
              <img
                src={attachment.url}
                alt={attachment.filename}
                loading="lazy"
                style={{
                  width: 132,
                  height: 84,
                  objectFit: 'cover',
                  border: '1px solid var(--rule-bright)',
                  borderRadius: 'var(--radius)',
                  display: 'block',
                }}
              />
            </a>
          </li>
        ) : (
          <li key={attachment.id} className="mono-note">
            {attachment.filename} (unavailable)
          </li>
        ),
      )}
    </ul>
  )
}
